// ============================================================
// ByOurConsent.com -- API Utility
// Axios instance with auth token and base URL
// ============================================================

import axios from 'axios';
import toast from 'react-hot-toast';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5002/api',
    // Must stay comfortably longer than the backend's own timeouts
    // (the AI generation endpoint allows up to 50s) -- otherwise the
    // frontend gives up and shows an error before the backend even
    // has a chance to finish and respond.
    timeout: 60000,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Attach JWT token to every request
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('boc_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Handle responses and errors globally
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response) {
            const status  = error.response.status;
            const message = error.response.data?.error || 'An error occurred';

            if (status === 401) {
                // Token expired or invalid
                localStorage.removeItem('boc_token');
                localStorage.removeItem('boc_user');
                // Don't redirect here -- let components handle it
            }

            if (status === 429) {
                toast.error('Too many requests. Please slow down.');
            }

            if (status >= 500) {
                toast.error('Server error. Please try again.');
            }

            return Promise.reject({ status, message, data: error.response.data });
        }

        if (error.request) {
            // Network error
            toast.error('Cannot connect to server. Check your connection.');
            return Promise.reject({ message: 'Network error' });
        }

        return Promise.reject(error);
    }
);

export default api;
